import { isEmpty } from "lodash";
import React, { useState, useEffect } from "react";

import { Collapse } from "react-collapse";
import { longDateTime } from "../../utils/DateHelper";

import { getSortDirectionIcon, toggleSort } from "../../utils/FilterHelpers";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import InstructionNotification from "../Notifications/InstructionNotification";
import Pagination from "../Pagination/Pagination";
import MissingData from "../Shared/MissingData";
import LazyImage from "../Shared/LazyImage";
import { IMAGE_MAP } from "../../utils/constants";

const NestedTable = ({
    headers,
    subHeaders,
    keys,
    subKeys,
    rows,
    subRowsKey,
    isLoading,
    isFilterUsed,
    entity,
    includeText,
    path = null,
    clickCallback = null,
    currentPage,
    onPageChange,
    totalCount,
    sizePerPage,
    handleItemsPerPage,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    purpose,
    includeMainRowImage = false,
    subTableColspan,
    missingSubRowMessage = "---",
    tableClassName = "",
}) => {
    const [isOpened, setIsOpened] = useState({});

    useEffect(() => {
        setIsOpened({});
    }, [rows]);

    const handleExpand = (key) => {
        key && setIsOpened((prevVal) => ({ ...prevVal, [key]: !prevVal[key] }));
    };

    const getTableData = (
        node,
        row,
        index,
        isShowCollapseIcon,
        isMainRow,
        image
    ) => {
        if (index === 0 && isShowCollapseIcon) {
            return (
                <td
                    className="px-6 py-3 whitespace-wrap"
                    key={`${isMainRow ? "main" : "inner"}-${index}`}
                >
                    <div className="flex items-center mr-10 ">
                        <i
                            className={`fas fa-caret-${
                                isOpened[row.id] ? "down" : "right"
                            } mr-2 ${
                                isEmpty(row[subRowsKey]) ? "invisible" : ""
                            }`}
                        />
                        {includeMainRowImage && (
                            <LazyImage
                                src={
                                    row.image ||
                                    require(`../../assets/images/${image}`)
                                        .default
                                }
                                className="table-user-img"
                                alt="UserImage"
                            />
                        )}
                        {node}
                    </div>
                </td>
            );
        }

        return (
            <td
                key={`${isMainRow ? "main" : "inner"}-${index}`}
                className="px-6 py-3 whitespace-wrap"
            >
                {node || "---"}
            </td>
        );
    };

    const renderRows = (keysMap, row, isMainRow, outerRows, outerIndex) => {
        const image = IMAGE_MAP[purpose];
        return keysMap?.map((key, index) => {
            if (key.role) return;
            const isShowCollapseIcon = index === 0 && isMainRow;
            const keyValue = getKeyValue(
                row,
                key,
                index,
                outerRows,
                outerIndex
            );
            return getTableData(
                keyValue,
                row,
                index,
                isShowCollapseIcon,
                isMainRow,
                image
            );
        });
    };

    const renderSubRows = (innerKeys, innerRows, mainRows, mainIndex) => {
        if (isEmpty(innerRows)) {
            return (
                <InstructionNotification
                    colSpan={innerKeys.length}
                    message={missingSubRowMessage}
                />
            );
        }

        return (
            innerRows &&
            innerRows.map((innerRow, index) => {
                return (
                    <tr
                        key={`innerRow-${innerRow.id || index}`}
                        className="tr-ele"
                    >
                        {renderRows(
                            innerKeys,
                            innerRow,
                            false,
                            mainRows,
                            mainIndex
                        )}
                    </tr>
                );
            })
        );
    };

    const getKeyValue = (row, key, index, mainRows, mainIndex) => {
        //Checking for inner keys for example user.organization.id
        if (key.element) return key.element(row, index, mainRows, mainIndex);
        let rowValue = key.innerKey
            ? row[key?.name]?.[key?.innerKey]
            : row[key?.name];
        rowValue = key.parseMethod ? key.parseMethod(rowValue) : rowValue;
        if (rowValue instanceof Date) return longDateTime(rowValue);
        return rowValue;
    };

    const renderInnerTable = (row, index) => {
        return (
            <td
                colSpan={subTableColspan || headers.length}
                className="bg-white"
            >
                <Collapse isOpened={isOpened[row.id]}>
                    <div className="table-responsive overflow-hidden border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y px-8 pb-4">
                        <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center">
                            <thead>
                                <tr className="bg-white">
                                    {renderHeadersData(subHeaders)}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {renderSubRows(
                                    subKeys,
                                    row?.[subRowsKey] || [],
                                    row,
                                    index
                                )}
                            </tbody>
                        </table>
                    </div>
                </Collapse>
            </td>
        );
    };

    const renderRowsData = () => {
        return (
            rows &&
            rows.map((row, index) => {
                return (
                    <React.Fragment key={`row-${row.id || index}`}>
                        <tr
                            className={`tr-ele cursor-pointer ${
                                index % 2 === 0 ? "even" : "odd"
                            }`}
                            onClick={() =>
                                !isEmpty(row[subRowsKey]) &&
                                handleExpand(row.id)
                            }
                        >
                            {renderRows(keys, row, true, null, index)}
                        </tr>
                        <tr hidden={!isOpened[row.id]} key={`inner-${index}`}>
                            {renderInnerTable(row, index)}
                        </tr>
                    </React.Fragment>
                );
            })
        );
    };

    const renderHeadersData = (data) => {
        return (
            data &&
            data.map((header, index) => {
                if (!header.role) {
                    return (
                        <th
                            key={`headers-${index}`}
                            scope="col"
                            className="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"
                        >
                            <div className="flex items-center justify-center">
                                {!header.skipSort && (
                                    <i
                                        onClick={() =>
                                            toggleSort(
                                                header.name,
                                                sortBy,
                                                setSortBy,
                                                sortDirection,
                                                setSortDirection
                                            )
                                        }
                                        className={`fas ${getSortDirectionIcon(
                                            header.name,
                                            sortBy,
                                            sortDirection
                                        )} table-head-icon`}
                                    />
                                )}
                                <span className="ml-2">{header.label}</span>
                            </div>
                        </th>
                    );
                }
            })
        );
    };

    return (
        <>
            <LoadingOverlay active={isLoading}>
                <div className={`card-table shadow ${tableClassName}`}>
                    {!isEmpty(rows) ? (
                        <>
                            <div className="flex flex-col mb-5">
                                <div className="table-responsive action-wrapper shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                                    <table className="table-striped custom min-w-full divide-y divide-gray-200 text-xs text-center">
                                        <thead className="parent-head">
                                            <tr>
                                                {renderHeadersData(headers)}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {renderRowsData()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {currentPage && (
                                <Pagination
                                    currentPage={currentPage}
                                    setCurrentPage={onPageChange}
                                    totalCount={totalCount}
                                    sizePerPage={sizePerPage}
                                    handleItemsPerPage={handleItemsPerPage}
                                />
                            )}
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={isFilterUsed}
                            entity={entity}
                            includeText={includeText}
                            path={path}
                            clickCallback={clickCallback}
                        />
                    )}
                </div>
            </LoadingOverlay>
        </>
    );
};

export default NestedTable;
